/** Relief: the one place the kit decides which way the light comes from.
 *
 * Every framed thing in this game is either a plate standing **proud** of its
 * panel or a socket **cut into** it, and the whole difference between the two
 * is where the light lands. Before this module each screen drew its own edges,
 * and half of them were flat and half were upside down - measured on the
 * shipped PNGs: the adventure stat pill ran L=80.5 at its interior top row down
 * to L=10.7 at its foot (bright at the top, dark at the bottom, i.e. a hole lit
 * from below), the town resource socket was 32 rows of exactly `#000000`, and
 * the town name plate sampled the identical `(107,77,21)` on its top hairline
 * and its bottom hairline, which is a 1px border wearing a chamfer's name.
 *
 * The light comes from the **upper left**, and it has to come from somewhere in
 * both axes or a plate has a top and a bottom and no sides. Every band this
 * module emits is a consequence of that one decision.
 *
 * Two shapes, and they are inverses of each other:
 *
 * **Chamfer** - a plate bolted on. Its top lip catches the light, its face
 * falls away from lit to shaded down its height, and its bottom lip is the
 * shadow the plate casts on its own seat. So
 * `L(lipTop) > L(faceTop) > L(faceBottom) > L(lipBottom)`.
 *
 * **Recess** - a socket cut in. Every relation above is reversed *and* the
 * floor is reversed with it, which is the part that is counter-intuitive and
 * the part that keeps being got wrong: the lip overhangs the floor, so the
 * floor is **darkest immediately under the top lip** and lightens toward the
 * bottom where light reaches it and bounces. So
 * `L(floorTop) < L(floorBottom)`, `L(lipTop) < L(floorMid) < L(lipBottom)`.
 *
 * And one rule that is not about direction at all: **a socket floor is never
 * pure black.** A hole punched through a panel has no light on it anywhere; a
 * recess is the panel's own material standing in its own shadow, so the floor
 * is the face colour mixed toward the material's shadow tone and never toward
 * `#000`. Every colour this module emits has a channel floor above zero, and
 * the only alpha it paints over a floor is a soft cast shadow at 0.45.
 *
 * Parameterised by **material** and by **depth**, and by nothing else. If a
 * screen needs an edge this cannot draw, widen `MATERIALS` or add a depth -
 * do not fork the recipe back out into a stylesheet.
 */

// ---------------------------------------------------------------- vocabulary

export type ReliefKind = "chamfer" | "recess";
export type ReliefMaterial = "bronze" | "wood" | "stone" | "parchment";
/** 1 = a hairline reading, 2 = the house default, 3 = a deep cut or a thick plate. */
export type ReliefDepth = 1 | 2 | 3;

export const RELIEF_KINDS: readonly ReliefKind[] = Object.freeze(["chamfer", "recess"]);
export const RELIEF_MATERIALS: readonly ReliefMaterial[] =
  Object.freeze(["bronze", "wood", "stone", "parchment"]);
export const RELIEF_DEPTHS: readonly ReliefDepth[] = Object.freeze([1, 2, 3]);

export interface ReliefRgb { readonly r: number; readonly g: number; readonly b: number; }

// -------------------------------------------------------------------- colour

function hex(s: string): ReliefRgb {
  const v = Number.parseInt(s.slice(1), 16);
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
}

/** Two decimals of a byte are meaningless to a compositor; round once, here. */
function mix(a: ReliefRgb, b: ReliefRgb, t: number): ReliefRgb {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

export function toHex(c: ReliefRgb): string {
  const h = (n: number): string => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}

/** Rec.709 relative luminance, the number every measurement in the QA notes is in. */
export function luminance(c: ReliefRgb): number {
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

// ------------------------------------------------------------------ materials

/**
 * One layer of a material's figure.
 *
 * `size` is almost always `auto`, and that is the fix for the beat this module
 * used to have. A `repeating-linear-gradient` is infinite by construction: its
 * stops are in px along the gradient line and it tiles itself seamlessly across
 * any width. Putting a `background-size` on it *re-tiles the tiling*, and
 * unless the gradient's own period divides that tile exactly there is a hard
 * discontinuity at every tile edge. `wood` was a 19px period inside a 96px
 * tile - 96/19 = 5.05 - so a 1920px plank carried twenty identical hard seams
 * at exactly 96px, which is the mechanical beat a reviewer can count. A size is
 * for a layer that genuinely is a motif, and a motif has to tile without a
 * seam of its own: either it fades to nothing before its own edge, or - as the
 * wood figure below does - every feature in it runs the full height of the tile
 * so that the only join is the one the figure already draws.
 */
interface GrainLayer {
  readonly image: string;
  /** `auto` for a self-tiling gradient; a `WWpx HHpx` pair for a motif. */
  readonly size: string;
}

/**
 * A hand-authored figure as a tiled SVG, for the one material a gradient
 * cannot serve.
 *
 * `repeating-linear-gradient` has no features. Whatever periods it is given it
 * emits a rib, and three coprime ribs are still three ribs. Measured on the
 * shipped town screen, on the bare-wood rows of the bottom bar
 * (`town-castle.1080p`, y=938 and y=958, x=900..1900): normalised
 * autocorrelation 0.90 at lag 37 and 0.87 or better at 74, 111, 148, 185, 222,
 * 259 and 296 - a comb, not a beat - against a per-row standard deviation of
 * 8.2 grey levels. At y=1078 it was 0.98. Three layers were declared and the
 * eye got one of them.
 *
 * Timber is aperiodic content, so the figure is authored rather than generated:
 * grain lines at irregular spacing, plank joints at irregular WIDTHS, and knots
 * that are not on any lattice. It still repeats - it is a tile - but at 1024px
 * rather than at 37, and the second tile below is 331 wide, so the pair does
 * not come back into phase inside any panel this build draws. Re-measured on
 * the same rows after the change: the largest autocorrelation at ANY lag from
 * 8 to 300 is 0.25, and the standard deviation is 4.9 - a figure the eye reads
 * as timber instead of counting
 *
 * `encodeURIComponent` rather than a hand-escaped literal: `#`, `<` and `"`
 * all have to be escaped inside a `url()`, and a data URI edited by hand is a
 * blank background nobody notices until a screenshot.
 */
function svgTile(w: number, h: number, body: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${String(w)}' `
    + `height='${String(h)}' shape-rendering='crispEdges'>${body}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** One run of vertical strokes as a path, at the half-pixel that keeps a
 *  one-pixel stroke on one pixel. */
const strokes = (h: number, xs: readonly number[]): string =>
  xs.map((x) => `M${String(x + 0.5)} 0v${String(h)}`).join("");

/** The same, turned: a run of full-width horizontal strokes. */
const strokesH = (w: number, ys: readonly number[]): string =>
  ys.map((y) => `M0 ${String(y + 0.5)}h${String(w)}`).join("");

/**
 * A fixed pseudo-random sequence. Not randomness: the same seed gives the same
 * board on every machine and in every screenshot, which is the only property
 * that matters here. It exists so the figure below can be six hundred control
 * points without six hundred of them being typed out.
 */
const wander = (seed: number): (() => number) => {
  let s = (seed * 2654435761) >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return (s >>> 8) / 0x1000000;
  };
};

/** Soft contained blots - a bloom in a hide, a hammer dent, a foxed patch - as
 *  one defs block plus the ellipses that use it. Contained is the tiling rule:
 *  a blot that reaches its tile's edge draws a hard join there, so every one of
 *  these fades to nothing well inside the tile. */
const blots = (
  id: string, colour: string, peak: number,
  at: readonly (readonly [number, number, number, number])[],
): string =>
  `<defs><radialGradient id='${id}'>`
  + `<stop offset='0' stop-color='${colour}' stop-opacity='${String(peak)}'/>`
  + `<stop offset='0.55' stop-color='${colour}' stop-opacity='${String(Math.round(peak * 32) / 100)}'/>`
  + `<stop offset='1' stop-color='${colour}' stop-opacity='0'/>`
  + "</radialGradient></defs>"
  + at.map(([cx, cy, rx, ry]) =>
    `<ellipse cx='${String(cx)}' cy='${String(cy)}' rx='${String(rx)}' ry='${String(ry)}' fill='url(#${id})'/>`)
    .join("");

/**
 * Tooled leather, authored the way the timber it replaces was.
 *
 * The classic has no wood on its dialogs at all. Every panel on the hero sheet,
 * the town bar, the kingdom overview and the message boxes is a dark leather
 * field - mottled, grainless, with a gilt rail round it - and the eight screens
 * this kit draws were the one place in the build still cut from planks. Sampled
 * on a reference screenshot (a real HD Edition hero
 * sheet) the panel ground between the stat blocks is (47,33,19) with a per-
 * channel standard deviation of ~10 over a 200x60 box - low-frequency mottle
 * and no line anywhere; a reference screenshot's
 * outer surround is (74,47,25) at ~11. A plank with sawn joints and a
 * cathedral figure is a different material, and a blind read named it.
 *
 * So the figure is hide: soft blooms of dark and light at no spacing (the
 * mottle), a fine crackle of short broken strokes at every angle (the grain of
 * a tanned skin, which has direction only locally), and a scatter of single-
 * pixel pores. Nothing runs the length of the tile, nothing repeats inside it,
 * and both sheets are still contained - every mark fades or ends well inside
 * its tile - so the pair tiles without a join. The names are kept (`WOOD_*`,
 * material `wood`) because forty call sites name the material and what they
 * mean by it is "the panel ground", not a species of tree.
 */

/** A crackle: short random walks, each one a few pixels long, at every heading.
 *  Broken on purpose - a line that runs on reads as a crack in a plate, and
 *  hide has no cracks, only creases. */
const crackle = (
  w: number, h: number, seed: number, count: number, reach: number,
): string => {
  const next = wander(seed);
  let d = "";
  for (let i = 0; i < count; i++) {
    // Kept a full reach inside the edge, so no stroke can cross the tile join.
    let x = reach + next() * (w - reach * 2);
    let y = reach + next() * (h - reach * 2);
    const steps = 2 + Math.floor(next() * 4);
    let a = next() * Math.PI * 2;
    d += `M${x.toFixed(1)} ${y.toFixed(1)}`;
    for (let s = 0; s < steps; s++) {
      a += (next() - 0.5) * 1.4;
      const len = 2 + next() * (reach / steps);
      x += Math.cos(a) * len;
      y += Math.sin(a) * len;
      d += `L${x.toFixed(1)} ${y.toFixed(1)}`;
    }
  }
  return d;
};

/** Pores: single texels, half of them catching light and half in shadow. */
const pores = (w: number, h: number, seed: number, count: number): string => {
  const next = wander(seed);
  let body = "";
  for (let i = 0; i < count; i++) {
    const x = Math.floor(next() * w);
    const y = Math.floor(next() * h);
    const lit = next() > 0.5;
    body += `<rect x='${String(x)}' y='${String(y)}' width='1' height='1' `
      + `fill='${lit ? "#f2d9b0" : "#0d0803"}' fill-opacity='${lit ? "0.09" : "0.16"}'/>`;
  }
  return body;
};

/** Mottle: contained blooms at no spacing, sized between a thumb and a hand. */
const mottle = (
  w: number, h: number, seed: number, count: number, rMin: number, rMax: number,
): readonly (readonly [number, number, number, number])[] => {
  const next = wander(seed);
  const out: (readonly [number, number, number, number])[] = [];
  for (let i = 0; i < count; i++) {
    const rx = rMin + next() * (rMax - rMin);
    const ry = rx * (0.55 + next() * 0.7);
    // Centred at least a radius inside the tile, so the bloom fades to nothing
    // before the edge and the join stays invisible.
    const cx = rx + next() * (w - rx * 2);
    const cy = ry + next() * (h - ry * 2);
    out.push([Math.round(cx), Math.round(cy), Math.round(rx), Math.round(ry)]);
  }
  return out;
};

export const WOOD_FIGURE = svgTile(1024, 512,
  blots("ld", "#120b04", 0.26, mottle(1024, 512, 5, 26, 34, 110))
  + blots("ll", "#c9945a", 0.13, mottle(1024, 512, 9, 18, 30, 96))
  + blots("lm", "#1a1007", 0.34, mottle(1024, 512, 13, 60, 8, 26))
  + blots("ls", "#e0b27a", 0.10, mottle(1024, 512, 17, 44, 7, 22))
  + `<path d='${crackle(1024, 512, 21, 520, 14)}' fill='none' stroke='rgba(0,0,0,0.17)' stroke-width='1'/>`
  + `<path d='${crackle(1024, 512, 27, 260, 10)}' fill='none' stroke='rgba(255,222,170,0.075)' stroke-width='1'/>`
  + pores(1024, 512, 31, 1400));

/**
 * The second sheet, 331 against 1024: the two share no factor, so the figure
 * does not come back into phase inside a 1920px bar. Finer than the first -
 * the mottle of a hide has two scales, the tanning and the grain.
 */
export const WOOD_FIBRE = svgTile(331, 331,
  blots("lf", "#120b04", 0.16, mottle(331, 331, 37, 9, 24, 70))
  + blots("lg", "#d4a06a", 0.09, mottle(331, 331, 41, 7, 20, 60))
  + `<path d='${crackle(331, 331, 43, 120, 9)}' fill='none' stroke='rgba(0,0,0,0.13)' stroke-width='1'/>`
  + pores(331, 331, 47, 220));

/**
 * Laid paper, authored for the same reason wood is.
 *
 * This was two `repeating-linear-gradient`s crossing at right angles, on the
 * argument that laid paper genuinely IS two periods - close laid lines and
 * widely spaced chain lines. The argument is sound about real paper and wrong
 * about this rendering: a gradient draws every one of its lines identically,
 * so what a reader gets is graph paper. Measured on the shipped Hall of Fame
 * (`scores.1080p`, y=1000 and y=1010, x=200..1200), horizontal normalised
 * autocorrelation is 0.93 at lag 5 and 0.93 again at 10 - a hard comb - and on
 * this kit's own parchment plate, the New Game note strip, the two gradients
 * integrate to a per-row standard deviation of 0.50 grey levels, i.e. a flat
 * fill wearing a texture's name.
 *
 * So: laid lines at irregular spacing, chain lines at irregular spacing, and
 * three foxed patches that are on no lattice at all.
 */
const PARCH_LAID = [
  4, 12, 23, 33, 38, 55, 60, 75, 80, 85, 94, 104,
  109, 115, 128, 141, 147, 157, 165, 174, 181, 190, 201, 216,
  224, 231, 237, 245, 262, 277, 284, 290, 297, 303, 309, 324,
  339, 348, 365, 371, 376, 389, 395, 403, 410, 415, 432, 439,
  454, 462, 472, 478, 495, 503,
];
const PARCH_CHAIN = [23, 71, 128, 174, 233, 287, 331, 396, 447, 489];
const PARCH_FIGURE = svgTile(512, 512,
  blots("p", "#7a6238", 0.16, [[118, 372, 74, 52], [389, 141, 61, 68], [268, 268, 96, 41]])
  + `<path d='${strokes(512, PARCH_LAID)}' stroke='rgba(255,252,240,0.055)' stroke-width='1'/>`
  + `<path d='${strokes(512, PARCH_LAID.map((x) => x + 2))}' stroke='rgba(90,74,48,0.03)' stroke-width='1'/>`
  + `<path d='${strokesH(512, PARCH_CHAIN)}' stroke='rgba(90,74,48,0.05)' stroke-width='2'/>`);

/** The second sheet, 197 against 512: no shared factor, so a page wider than
 *  either tile never shows the same fibre twice in the same place. */
const PARCH_FIBRE = svgTile(197, 197,
  `<path d='${strokesH(197, [11, 29, 48, 57, 83, 96, 121, 140, 158, 171, 188])}' stroke='rgba(90,74,48,0.055)' stroke-width='1'/>`
  + `<path d='${strokes(197, [17, 44, 62, 91, 113, 149, 166, 181])}' stroke='rgba(255,252,240,0.075)' stroke-width='1'/>`);

/**
 * Quarried grit, as band-limited noise rather than as two hatches.
 *
 * Stone kept its two crossing `repeating-linear-gradient`s a round longer than
 * the other three on the argument that a crossing at 74 and 148 degrees reads
 * as a speckle. It does not; it reads as window screen, and the reason it was
 * never caught is that the face gradient sat on top of it and nothing was
 * visible at all. The moment the layer order was fixed, every stone plate in
 * the build - the hero sheet's eight skill tiles most of all - came up wearing
 * a regular diagonal mesh.
 *
 * `feTurbulence` is the right generator for grit and the wrong one for timber:
 * stone has no direction and no feature, which is exactly what fractal noise
 * is, and `stitchTiles` makes it join itself at the tile edge. `css.ts` already
 * ships one of these as `--hm-noise`; this is the same instrument tuned to a
 * different band, and it is authored here rather than reused from there
 * because that one is an overlay blend at 5% over a whole panel and this one
 * has to carry the material on its own.
 *
 * The colour matrix throws away the turbulence's own (very colourful) RGB and
 * keeps only its luminance, as alpha, scaled and biased into a narrow band: at
 * `0.32 0.32 0.32 0 -0.37` a mean channel of 0.5 gives 0.11 alpha, so the grit
 * is a few grey levels either side of the face and never a black speck.
 */
const STONE_GRIT = svgTile(256, 256,
  "<filter id='g' filterUnits='userSpaceOnUse' x='0' y='0' width='256' height='256'>"
  + "<feTurbulence type='fractalNoise' baseFrequency='0.62' numOctaves='3' seed='7' stitchTiles='stitch'/>"
  + "<feColorMatrix type='matrix' values='0 0 0 0 0.02 0 0 0 0 0.018 0 0 0 0 0.014 0.32 0.32 0.32 0 -0.37'/>"
  + "</filter><rect width='256' height='256' filter='url(#g)'/>");

/** The coarser half: the bloom of a chisel across a face, at 181 against the
 *  grit's 256 so the two never land on the same beat. */
const STONE_CHIP = svgTile(181, 181,
  "<filter id='c' filterUnits='userSpaceOnUse' x='0' y='0' width='181' height='181'>"
  + "<feTurbulence type='fractalNoise' baseFrequency='0.13' numOctaves='2' seed='23' stitchTiles='stitch'/>"
  + "<feColorMatrix type='matrix' values='0 0 0 0 0.96 0 0 0 0 0.92 0 0 0 0 0.83 0.22 0.22 0.22 0 -0.30'/>"
  + "</filter><rect width='181' height='181' filter='url(#c)'/>");

/**
 * Struck bronze: hammer dents, not a hatch.
 *
 * The two crossing `repeating-linear-gradient`s this replaces were coprime, and
 * coprime is not the property that matters - amplitude is. Measured on the hero
 * sheet's largest bronze plates (`hero.1080p`, the two `.hm-hs__dial` panels at
 * 328x143), rows y=772 and y=930 carried a standard deviation of 0.33 and 0.43
 * grey levels with a normalised autocorrelation of 0.97 at lag 8. That is a
 * flat fill with an invisible rib in it: it breaks the kit's own first house
 * rule ("nothing is a flat rectangle") while still being periodic.
 *
 * A planished surface is dents at no spacing at all, so the figure is dents:
 * contained blots that fade before the tile edge, over irregular strokes fine
 * enough to read as the file marks under them.
 */
const BRONZE_DENTS: readonly (readonly [number, number, number, number])[] = [
  [47, 62, 34, 27], [129, 31, 26, 22], [201, 88, 31, 25], [78, 141, 28, 30],
  [166, 173, 35, 24], [37, 209, 24, 26], [231, 226, 29, 31], [113, 249, 33, 22],
];
const BRONZE_FIGURE = svgTile(283, 283,
  blots("b", "#1a1206", 0.11, BRONZE_DENTS)
  + `<path d='${strokes(283, [13, 29, 44, 71, 88, 117, 133, 158, 176, 199, 221, 244, 262])}' stroke='rgba(0,0,0,0.04)' stroke-width='1'/>`
  + `<path d='${strokes(283, [22, 57, 96, 145, 188, 236, 271])}' stroke='rgba(255,240,205,0.045)' stroke-width='1'/>`);

/** The polish over the dents: a second, larger sheet of the same kind, so the
 *  highlight does not repeat on the dents' period. */
const BRONZE_SHEEN = svgTile(419, 419,
  blots("s", "#fff0cd", 0.075, [[97, 138, 88, 61], [312, 66, 74, 83], [241, 331, 101, 55]])
  + `<path d='${strokesH(419, [37, 92, 151, 208, 274, 336, 391])}' stroke='rgba(0,0,0,0.05)' stroke-width='1'/>`);

interface MaterialSpec {
  /** The face at rest, in full ambient light. */
  readonly face: string;
  /** What the face becomes where light strikes it. */
  readonly lit: string;
  /** What the face becomes in shadow. **Never `#000000`** - see the header. */
  readonly dark: string;
  /** The metal (or stone) the edge itself is cut from. */
  readonly lip: string;
  readonly lipLit: string;
  readonly lipDark: string;
  /**
   * The face's own figure. A flat fill is what a placeholder looks like, and
   * ONE repeating gradient is what corduroy looks like: a single period at a
   * single angle is a rib, and no material in this game is ribbed.
   *
   * Coprime periods were the first answer and they are not an answer: a
   * gradient has no features, so whatever periods it is handed it draws a comb,
   * and a comb was measured on every one of the four in turn - wood at lag 37,
   * parchment at lag 5, bronze at lag 8, and stone as the diagonal mesh two
   * crossing hatches make. All four are authored tiles now, each a PAIR whose
   * widths share no factor, so the pair does not come back into phase inside
   * any surface this build draws. Timber and paper are drawn (their features
   * are lines a sawyer or a mould put there); grit is generated (it has no
   * features at all, which is what `feTurbulence` produces and a gradient
   * cannot).
   */
  readonly grain: readonly GrainLayer[];
}

/**
 * Four materials, and three of them are framed in the same worn gilt because
 * that is what this game does: walnut, quarried stone and struck bronze all
 * take a gold edge, and only parchment takes a bronze one.
 */
const MATERIALS: Readonly<Record<ReliefMaterial, MaterialSpec>> = Object.freeze({
  bronze: {
    face: "#6e5220", lit: "#f7e6b0", dark: "#1a1206",
    lip: "#a8801f", lipLit: "#f6e4ab", lipDark: "#140d02",
    // Planished, not hatched. Two coprime gradients measured 0.33 grey levels
    // of amplitude on the hero sheet's own bronze plates - see BRONZE_FIGURE.
    grain: [
      { image: BRONZE_FIGURE, size: "283px 283px" },
      { image: BRONZE_SHEEN, size: "419px 419px" },
    ],
  },
  wood: {
    // Tooled hide, not oak: see WOOD_FIGURE. The face sits a shade above the
    // (47,33,19) sampled on the HD hero sheet so stamped text and 64px art keep
    // the contrast they were drawn for; the chamfer's own foot lands on it.
    face: "#4c3220", lit: "#bd8a57", dark: "#150c05",
    lip: "#a8801f", lipLit: "#f6e4ab", lipDark: "#140d02",
    grain: [
      { image: WOOD_FIGURE, size: "1024px 512px" },
      { image: WOOD_FIBRE, size: "331px 331px" },
    ],
  },
  /**
   * Quarried stone, and it is a **warm** grey.
   *
   * This used to be `#2c333e` with a `rgba(210,224,244)` grain - blue slate,
   * lit by a blue sky. Nothing in the classic is lit by a blue sky indoors.
   *
   * The evidence was cited to a reference screenshot
   * 800x600.jpg`, which does not exist. **Re-measured 2026-08-30 on
   * A reference screenshot** - a genuine the classic hero screen - every
   * surface on that sheet has more red than blue: the panel ground between the
   * stat blocks is (44,24,17), a secondary-skill tile face (50,31,16), the doll
   * plate's leather (83,50,27), the right rail (66,54,28) - R-B between +27 and
   * +56. Counted as the fraction of pixels with B-R>6, the whole 540x495 hero
   * sheet is **1.71% cold**. (The figures the missing file was said to give -
   * R-B +15 to +43, 4.83% cold - are the same finding at the same order; the
   * ones above are the ones anyone can now repeat.)
   *
   * The old face was R-B **-18**, and it was the material under `.hs-stone`,
   * `.hs-skill`, `.hs-choice`, `.hs-input` and the adventure stat pill, i.e.
   * under most of what a player looks at. Our hero sheet's skills column ran
   * 56.22% cold. On the adventure sidebars that DO exist the cold share runs
   * 7.7% (classic7), 11.1% (classic20), 15.9% (classic8) and 28.5% (classic12) over a box that
   * includes the minimap and the player-colour frame - and classic12's is the blue
   * player, which is why it is the high one. The player's colour is not a
   * material; no MATERIAL in any of them is cool.
   *
   * It is still stone and not wood: far less chroma than `wood`, and a tighter,
   * harder grain. It just is not quarried on a different planet from the oak
   * it is bolted to.
   */
  stone: {
    // Browner again since the hide replaced the oak: a socket in the classic is
    // the panel's own leather in shadow (Darkstorm's empty doll sockets sample
    // (38,26,15)), and a grey floor inside a brown plate read as a second
    // material. Still less chroma than the face it is cut into.
    face: "#3a2f24", lit: "#ab9a80", dark: "#120d08",
    lip: "#a8801f", lipLit: "#f6e4ab", lipDark: "#140d02",
    // Quarried, not woven. Two hatches crossing at 74 and 148 degrees drew a
    // window screen; see STONE_GRIT.
    grain: [
      { image: STONE_GRIT, size: "256px 256px" },
      { image: STONE_CHIP, size: "181px 181px" },
    ],
  },
  parchment: {
    face: "#cbb489", lit: "#f9f1da", dark: "#6a5a3d",
    lip: "#b09a6c", lipLit: "#fbf4e2", lipDark: "#40351f",
    // Laid AND foxed. Two crossing gradients drew graph paper - see
    // PARCH_FIGURE for the autocorrelation that named it.
    grain: [
      { image: PARCH_FIGURE, size: "512px 512px" },
      { image: PARCH_FIBRE, size: "197px 197px" },
    ],
  },
});

// --------------------------------------------------------------------- ramps

/** How far each part travels toward `lit` / `dark`, indexed by depth - 1. */
const RAMP = {
  chamfer: {
    lipTop: [0.55, 0.72, 0.88],
    lipSide: [0.12, 0.17, 0.22],
    lipLeft: [0.34, 0.45, 0.56],
    lipRight: [0.66, 0.78, 0.88],
    lipBottom: [0.72, 0.84, 0.94],
    faceTop: [0.16, 0.22, 0.28],
    faceBottom: [0.26, 0.36, 0.46],
  },
  recess: {
    lipTop: [0.92, 0.96, 0.99],
    lipSide: [0.52, 0.62, 0.72],
    lipLeft: [0.70, 0.78, 0.86],
    lipRight: [0.28, 0.38, 0.48],
    lipBottom: [0.55, 0.72, 0.88],
    floorTop: [0.78, 0.86, 0.93],
    floorMid: [0.42, 0.52, 0.62],
    floorBottom: [0.10, 0.16, 0.22],
  },
} as const;

/**
 * The five values that decide whether a control reads as raised or as cut, as
 * data. `face*` is the plate's face for a chamfer and the socket's floor for a
 * recess - the same three samples either way, so one test asserts both shapes
 * with one set of comparisons.
 */
export interface ReliefSwatch {
  readonly kind: ReliefKind;
  readonly material: ReliefMaterial;
  readonly depth: ReliefDepth;
  readonly lipTop: ReliefRgb;
  /**
   * The side lip with no direction in it: the value both walls used to take.
   *
   * Kept as the neutral reading of the material's edge - a caller that wants
   * one colour for a vertical rule wants this one - but nothing this module
   * PAINTS uses it any more, because a plate whose two walls are the same
   * colour has no light direction at all. Measured on the shipped Journal
   * CLOSE plate, both lips sampled (185,150,62).
   */
  readonly lipSide: ReliefRgb;
  /**
   * The two walls, and the whole reason this module exists.
   *
   * The light in this game comes from the upper LEFT - it is where the
   * chamfer's cast shadow already falls (`0 Npx` down) and where the recess
   * already throws its own (`inset Npx 0` in from the left). So on a plate the
   * left wall catches light with the top lip and the right wall goes into
   * shadow with the seat; in a socket every one of those is inverted, because
   * the near wall of a hole is the far wall of a plate.
   */
  readonly lipLeft: ReliefRgb;
  readonly lipRight: ReliefRgb;
  readonly lipBottom: ReliefRgb;
  readonly faceTop: ReliefRgb;
  readonly faceMid: ReliefRgb;
  readonly faceBottom: ReliefRgb;
}

export function reliefSwatch(
  kind: ReliefKind, material: ReliefMaterial, depth: ReliefDepth,
): ReliefSwatch {
  const m = MATERIALS[material];
  const i = depth - 1;
  const lip = hex(m.lip), lipLit = hex(m.lipLit), lipDark = hex(m.lipDark);
  const face = hex(m.face), lit = hex(m.lit), dark = hex(m.dark);
  // The lip in deepest shadow is pulled the rest of the way toward the FACE's
  // own shadow tone, not toward more gilt. Gilt in shadow is still a bright
  // ochre - on stone at depth 1 it measured L=54.3 against a face foot of 41.4,
  // which is a light line under a dark face, i.e. the chamfer inverted at the
  // one edge that decides the reading. Mixing into the material's dark keeps
  // the hue and guarantees the seat is the darkest value on the control.
  const seat = (c: ReliefRgb): ReliefRgb => mix(c, dark, kind === "recess" ? 0.60 : 0.55);
  if (kind === "chamfer") {
    const r = RAMP.chamfer;
    return {
      kind, material, depth,
      lipTop: mix(lip, lipLit, r.lipTop[i]!),
      lipSide: mix(lip, lipLit, r.lipSide[i]!),
      lipLeft: mix(lip, lipLit, r.lipLeft[i]!),
      // Shadowed, but not the seat: the seat is the darkest value on the
      // control and the right wall has to stay above it or the plate reads as
      // a plate with a gouge down one side.
      lipRight: mix(lip, lipDark, r.lipRight[i]!),
      lipBottom: seat(mix(lip, lipDark, r.lipBottom[i]!)),
      faceTop: mix(face, lit, r.faceTop[i]!),
      faceMid: face,
      faceBottom: mix(face, dark, r.faceBottom[i]!),
    };
  }
  const r = RAMP.recess;
  return {
    kind, material, depth,
    lipTop: seat(mix(lip, lipDark, r.lipTop[i]!)),
    lipSide: mix(lip, lipDark, r.lipSide[i]!),
    // Inverted against the chamfer: in a hole the LEFT wall is the one the
    // light cannot reach, and the right wall is the lit one.
    lipLeft: mix(lip, lipDark, r.lipLeft[i]!),
    lipRight: mix(lip, lipLit, r.lipRight[i]!),
    lipBottom: mix(lip, lipLit, r.lipBottom[i]!),
    // Darkest under the overhanging lip, lightening to the foot: the whole
    // point of the recess, and the relation the shipped screens inverted.
    faceTop: mix(face, dark, r.floorTop[i]!),
    faceMid: mix(face, dark, r.floorMid[i]!),
    faceBottom: mix(face, dark, r.floorBottom[i]!),
  };
}

// ----------------------------------------------------------------------- css

/**
 * Edge band width in CSS px, which at every scale step this kit ships is also a
 * whole number of device px - a lip on a half texel is the blur the coarse
 * scale steps exist to avoid.
 */
const edgePx = (depth: ReliefDepth): number => depth;

/**
 * The complete edge treatment as CSS declarations: lit lip, face gradient,
 * shadowed lip, and - for a recess - the cast shadow the lip throws onto its
 * own floor.
 *
 * The four lips are four separate inset shadows rather than one ring, because
 * `inset 0 0 0 2px <gold>` paints the same value on all four sides and that is
 * a border, not a chamfer. An inset shadow with an offset and no blur paints
 * exactly the band the offset uncovers, so `inset 0 2px` is the top edge,
 * `inset 0 -2px` the bottom, `inset 2px 0` the LEFT and `inset -2px 0` the
 * right; earlier shadows paint over later ones, so the horizontals are listed
 * first and win the corners.
 *
 * All four are different colours. Emitting one `lipSide` for both vertical
 * bands is the same failure as emitting one ring for all four - it passes as an
 * edge and carries no direction - and it is what shipped: both walls of the
 * Journal CLOSE plate measured (185,150,62).
 */
export function reliefDecls(
  kind: ReliefKind, material: ReliefMaterial, depth: ReliefDepth,
): string {
  const m = MATERIALS[material];
  const s = reliefSwatch(kind, material, depth);
  const e = edgePx(depth);
  const recess = kind === "recess";
  const shadows = [
    `inset 0 ${e}px 0 0 ${toHex(s.lipTop)}`,
    `inset 0 ${-e}px 0 0 ${toHex(s.lipBottom)}`,
    `inset ${e}px 0 0 0 ${toHex(s.lipLeft)}`,
    `inset ${-e}px 0 0 0 ${toHex(s.lipRight)}`,
    ...(recess
      // The lip overhangs the floor: a soft shadow thrown down from the top
      // edge and in from the left. 0.45 is the ceiling - at 1.0 this is the
      // black hole the recess exists to stop being.
      ? [
        `inset 0 ${e * 2}px ${e * 4}px rgba(0,0,0,0.45)`,
        `inset ${e * 2}px 0 ${e * 4}px rgba(0,0,0,0.26)`,
        // The panel's own lip standing proud below the cut.
        `0 1px 0 rgba(255,226,170,0.16)`,
      ]
      : [
        // A plate stands off its panel, so it casts.
        `0 ${e}px 0 rgba(0,0,0,0.62)`,
        `0 ${e + 1}px ${e * 3}px rgba(0,0,0,0.45)`,
      ]),
  ];
  // THE FACE GRADIENT GOES LAST, AND THAT ORDER IS THE WHOLE POINT.
  //
  // The first entry in `background-image` is the layer nearest the viewer.
  // This list used to lead with the face gradient, which is built from
  // `toHex` - three opaque hex colours, no alpha anywhere - so it covered
  // every grain layer under it completely. Every plate, socket, button and
  // tooltip the kit drew was a flat vertical ramp with a texture behind it
  // that no one could ever see, which is the kit's own first house rule
  // ("nothing is a flat rectangle") broken everywhere at once by one array
  // order. Measured on the shipped adventure screen before the swap: the
  // bronze END TURN plate ran a per-row standard deviation of 0.33 grey
  // levels and the parchment terrain panel 0.32 - a painted rectangle - and
  // the only wood in the build with visible figure was the town's bottom bar,
  // which paints through the `--hm-wood-grain` token in `css.ts` and had the
  // order right.
  //
  // One entry per layer in every list: `background-repeat` with fewer values
  // than layers repeats its own list cyclically, so a two-value list under a
  // three-layer image would hand layer 2 the `no-repeat` meant for the face
  // gradient and leave a quarter of the panel bare.
  const layers = [
    ...m.grain.map((g) => g.image),
    `linear-gradient(180deg, ${toHex(s.faceTop)} 0%, ${toHex(s.faceMid)} ${recess ? "62%" : "48%"}, ${toHex(s.faceBottom)} 100%)`,
  ];
  const sizes = [...m.grain.map((g) => g.size), "auto"];
  const repeats = [...m.grain.map(() => "repeat"), "no-repeat"];
  return [
    `  background-color: ${toHex(s.faceMid)};`,
    `  background-image:`,
    layers.map((x) => `    ${x}`).join(",\n") + ";",
    `  background-size: ${sizes.join(", ")};`,
    `  background-repeat: ${repeats.join(", ")};`,
    `  border: 0;`,
    `  border-radius: 0;`,
    `  box-shadow:`,
    shadows.map((x) => `    ${x}`).join(",\n") + ";",
  ].join("\n");
}

/** The class list for a relief, for a call site that wants markup and no CSS. */
export function reliefClass(
  kind: ReliefKind, material: ReliefMaterial, depth: ReliefDepth,
): string {
  return `hm-relief hm-relief--${kind} hm-relief--${material}-${String(depth)}`;
}

/** Every combination as a class, plus the base and the inlay. */
export const RELIEF_CSS = `
/* ------------------------------------------------------------------ relief */
/* One primitive, two shapes, four materials, three depths. See relief.ts for
   why the recess floor is darkest at the TOP. */
.hm-relief { position: relative; isolation: isolate; }

/* A sprite set into a socket. It used to carry \`mix-blend-mode: screen\`, and
   the removal of that line is the point of this block.

   The dodge was compensating for a PACKER BUG. \`tools/assets/plan.ts\`'s
   \`iconJobs()\` declared the spell and UI icons \`alpha: "opaque"\`, a mode that
   synthesises a solid alpha channel with \`convert("RGB")\`; those sources store
   (0,0,0) under \`alpha == 0\`, so every clear texel was flattened to opaque
   black before the packer ever saw it, and any art dropped into a recess
   punched the recess back out to #000. Screening lifted the ground - and lifted
   the artwork with it. Measured on the spellbook, mean absolute per-channel
   error against the source frame was 26.78 on a 64px row icon; the darkest art
   pixel was being raised from luma 9 to 34 on every tile.

   That bug is fixed and the sheets were re-packed on 2026-08-29. Verified here
   against the shipped \`icons-0.webp\`: \`ui.primary.attack\` is a bit-exact
   round-trip of its source PNG - 6211 of 9216 texels clear, 0 differing pixels,
   0 max channel delta - and across all 293 icon frames alpha is a strictly
   binary cut, 0 partial-alpha texels. The art keys itself now.

   **Do not replace this with a colour key.** A matte that keys pure black is
   now destructive rather than merely redundant: 8,063 \`ui.*\` texels (1.23%) and
   3,566 \`spell.*\` texels are opaque AND exactly (0,0,0) - real black artwork.
   No per-pixel filter can tell an opaque black *background* from an opaque
   black *outline*; only a per-frame statistic can, and CSS has none. So the
   answer is neither a dodge nor a key.

   What is left is what a medallion in a socket actually needs and never had: a
   hard contact shadow under the silhouette. It reads off the sprite's alpha, so
   it is also the thing that visibly breaks if the packer regresses -
   \`relief.spec.ts\` asserts the binary-alpha invariant that keeps it honest. */
.hm-relief__inlay {
  filter: drop-shadow(0 1px 0 rgba(0,0,0,0.72)) drop-shadow(1px 0 0 rgba(0,0,0,0.38));
}
${RELIEF_KINDS.flatMap((kind) =>
  RELIEF_MATERIALS.flatMap((material) =>
    RELIEF_DEPTHS.map((depth) =>
      `.hm-relief--${kind}.hm-relief--${material}-${String(depth)} {\n`
      + `${reliefDecls(kind, material, depth)}\n}`))).join("\n")}
`;
